import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress  from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import FormGroup from '@mui/material/FormGroup';
import InputLabel from '@mui/material/InputLabel';
import Paper, { PaperProps } from '@mui/material/Paper';
import Draggable from 'react-draggable';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import React, { ReactElement, useEffect, useState } from 'react';
import {  uuid } from '../../../../utils/utils';
import { toast } from 'react-toastify';
import { useWorkspaceContext } from '../../../../context/workspaceContext';
import { CanvasEnvironment } from '../../../../utils/canvas/canvas-environment';
import * as CanvasUtils from '../../../../utils/canvas/node-utils';
import { PsfTemplate, PsfTemplateItem, EventSequenceOccurrenceStats } from '../../../../@types/occurrence-dna';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import EventSequenceService from '../../../../services/event-sequence.service';
import ConditionBuilderComponent, { Condition } from '../../../form-components/ConditionBuilder.component';
import { Typography } from '@mui/material';
import { debounce } from '@mui/material/utils';

type ValuePiece = Date | null;
type Value = ValuePiece | [ValuePiece, ValuePiece];

function forceUTCDate(referenceDate: Value): string | null { 
    if (referenceDate === null) {
        return null;
    }
    if (Array.isArray(referenceDate)) {
        let start = referenceDate[0] as Date;
        let end = referenceDate[1] as Date;
        let tzOffset = start.getTimezoneOffset();
        let startDate = new Date();
        startDate.setTime(start.getTime() - tzOffset * 60 * 1000);
        let endDate = new Date();
        endDate.setTime(end.getTime() - tzOffset * 60 * 1000);
        return JSON.stringify([startDate, endDate]);
    }
    let tzOffset = referenceDate.getTimezoneOffset();
    let date = new Date();
    date.setTime(referenceDate.getTime() - tzOffset * 60 * 1000);
    return date.toJSON();
}


function PaperComponent(props: PaperProps): ReactElement {
    const nodeRef = React.useRef(null);
    return (
        <Draggable nodeRef={nodeRef} handle="#draggable-dialog-title" cancel={'[class*="MuiDialogContent-root"]'}>
            <Paper {...props} ref={nodeRef} />
        </Draggable>
    );
}

export default function AccidentDnaSummaryComponentModal(props: { open: boolean, env: CanvasEnvironment | null, onClose: () => void }): ReactElement {
    const { open, env, onClose } = props;
    const [esdPsfTemplate, setEsdPsfTemplate] = useState<PsfTemplate | null>(null);
    const [occurrenceTemplateKeys, setOccurrenceTemplateKeys] = useState<string[]>([]);
    const [selectedPsfItems, setSelectedPsfItems] = useState<PsfTemplateItem[]>([]);

    const [startValue, onChangeStartValue] = useState<Value>(new Date('2010-01-01T00:00:00.000'));
    const [endValue, onChangeEndValue] = useState<Value>(new Date('2050-01-01T00:00:00.000'));
    const [condition, setCondition] = useState<Condition | null>(null);
    const [isRunning, setIsRunning] = useState<boolean>(false);
    const [uniqueId, setUniqueId] = useState<string>('');
    const [name, setName] = useState<string>('');
    const [success, setSuccess] = useState<number>(0);
    const [failure, setFailure] = useState<number>(0);
    const {  currentCanvasSelectedNode } = useWorkspaceContext();
    const uuidRef = React.useRef(uuid());

    const debounceFetch = React.useMemo(
        () =>
            debounce(
                (
                    request: { esdId: number, start: string | null, end: string | null, searchTerm: string | null, psfItemIds: Array<number | string> | null },
                    callback: (results: EventSequenceOccurrenceStats) => void,
                ) => {
                    const fetchStats = async (esdId: number, start: string | null, end: string | null, searchTerm: string | null, psfItemIds: Array<number | string> | null): Promise<any> => {
                        try {
                            setIsRunning(true);
                            if (searchTerm) {
                                const [_key, _comparator, value] = searchTerm.split('||');
                                if (value.trim() === '') {
                                    callback({ eventSequenceId: esdId, numOccurrences: 0, eventOccurrenceStats: [], faultTreeNodeOccurrenceStats: [] } as EventSequenceOccurrenceStats);
                                    return;
                                }
                            }
                            const stats: EventSequenceOccurrenceStats = await EventSequenceService.fetchStatsOfOccurrencesLinkedToEventSequence(esdId, start, end, searchTerm, psfItemIds);
                            callback(stats);
                        }
                        catch (error: any) {
                            toast.error(error.message);
                        } finally {
                            setIsRunning(false);
                        }
                    }
                    
                    fetchStats(request.esdId, request.start, request.end, request.searchTerm, request.psfItemIds);
                },
                1000,
            ),
        [],
    );


    const onChangeCondition = (newCondition: Condition | null): void => {
        setCondition(newCondition); 
    }

    const isEvent = (): boolean => {
        if (!currentCanvasSelectedNode) {
            return false;
        }
        return CanvasUtils.isEvent(currentCanvasSelectedNode.type);
    }

    const isFaultTreeNode = (): boolean => {
        if (!currentCanvasSelectedNode) {
            return false;
        }
        return CanvasUtils.isFaultTreeNode(currentCanvasSelectedNode.type);
    }

    useEffect(() => {
        if (open) {
            const [selectedNode] = env!.selection
            setUniqueId(selectedNode.get('data').uniqueId);
            setName(selectedNode.get('data').name);
            setIsRunning(true);
            let eventSequenceId = env!.eventSequence!.id;
            let esdPsfTemplatePromise = EventSequenceService.getPsfTemplate(eventSequenceId);
            let templateKeysPromise = EventSequenceService.fetchOccurrenceTemplateKeys();
            let psfIds = selectedPsfItems.map((item) => item.id);
            let searchCondition = condition ? [condition.key, condition.comparator, condition.value].join('||') : null;
            let scores = EventSequenceService.fetchStatsOfOccurrencesLinkedToEventSequence(
            env!.eventSequence!.id,
            forceUTCDate(startValue),
            forceUTCDate(endValue),
            searchCondition,
            psfIds);
            setCondition(null);
            Promise.all([esdPsfTemplatePromise, templateKeysPromise, scores]).then((response) => {
                setEsdPsfTemplate(response[0]);
                setOccurrenceTemplateKeys(response[1]);
                if (isEvent()) {
                    let stat = response[2].eventOccurrenceStats.find((stat) => stat.safetyModelElementId === selectedNode.get('data').id);     
                    setSuccess(stat ? stat.numSuccesses : 0);
                    setFailure(stat ? stat.numFailures : 0);
                } else {
                    let stat = response[2].faultTreeNodeOccurrenceStats.find((stat) => stat.safetyModelElementId === selectedNode.get('data').id);
                    setSuccess(stat ? stat.numSuccesses : 0);
                    setFailure(stat ? stat.numFailures : 0);
                }
            }).catch((error) => {
                toast.error(error.message);
            }).finally(() => {
                setIsRunning(false);
            });
        }
    }, [open]);

    useEffect(() => {
        if (env) {
            let psfIds = selectedPsfItems.map((item) => item.id);
            let searchCondition = condition ? [condition.key, condition.comparator, condition.value].join('||') : null;
            const [selectedNode] = env!.selection;

            debounceFetch({ esdId: env!.eventSequence!.id, start: forceUTCDate(startValue), end: forceUTCDate(endValue), searchTerm: searchCondition, psfItemIds: psfIds}
                , (response: EventSequenceOccurrenceStats) => {
                    if (isEvent()) {
                        let stat = response.eventOccurrenceStats.find((stat) => stat.safetyModelElementId === selectedNode.get('data').id);
                        setSuccess(stat ? stat.numSuccesses : 0);
                        setFailure(stat ? stat.numFailures : 0);
                    } else {
                        let stat = response.faultTreeNodeOccurrenceStats.find((stat) => stat.safetyModelElementId === selectedNode.get('data').id);
                        setSuccess(stat ? stat.numSuccesses : 0);
                        setFailure(stat ? stat.numFailures : 0);
                    }
                });
        }

    }, [startValue, endValue, selectedPsfItems, condition]);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            PaperComponent={PaperComponent}
            aria-labelledby='draggable-dialog-title'
            fullWidth
            maxWidth="sm"
        >
            <DialogTitle  style={{ cursor: 'move'}} id='draggable-dialog-title'>
                Statistics
            </DialogTitle>
            <DialogContent dividers style={{ minHeight: '300px' }}>
                <Stack spacing={2} justifyContent='start'>
                    <TextField
                        label='Unique ID'
                        value={uniqueId}
                        disabled
                    />
                    <TextField
                        label='Name'
                        value={name}
                        disabled
                    />
                </Stack>
                <hr />
                <FormGroup>
                    <Stack direction='row' spacing={2} justifyContent='start'>
                        <InputLabel tabIndex={-1} id={`start-label-${uuidRef.current}`} htmlFor={`start-value-${uuidRef.current}`}>From: </InputLabel>                  
                        <DatePicker
                            id={`start-value-${uuidRef.current}`}
                            showIcon
                            toggleCalendarOnIconClick
                            selectsStart
                            selected={startValue as Date}
                            startDate={startValue as Date}
                            endDate={endValue as Date}
                            onChange={(date: Date | null): void => { onChangeStartValue(date);  }}
                            placeholderText='Select start date'
                        />
                        
                        
                        <InputLabel tabIndex={-1} id={`end-label-${uuidRef.current}`} htmlFor={`end-value-${uuidRef.current}`}>To: </InputLabel>         
                        <DatePicker
                            id={`end-value-${uuidRef.current}`}
                            showIcon
                            toggleCalendarOnIconClick
                            selectsEnd
                            selected={endValue as Date}
                            startDate={startValue as Date}
                            endDate={endValue as Date}  
                            minDate={startValue as Date}
                            onChange={(date: Date | null): void => { onChangeEndValue(date); }}
                            placeholderText='Select end date'
                        /> 
                    </Stack>
                    <Stack spacing={2} justifyContent='start' sx={{marginTop: '20px'}}>
                        {isFaultTreeNode() && <Autocomplete
                            options={esdPsfTemplate && esdPsfTemplate.psfTemplateItems ? esdPsfTemplate.psfTemplateItems.sort((a, b) => a.category.localeCompare(b.category)) : []}
                            getOptionLabel={(psfItem: PsfTemplateItem): string => psfItem.code ? `${psfItem.name} - ${psfItem.code}` : psfItem.name}
                            renderOption={(props, option: PsfTemplateItem): ReactElement => (
                                <li {...props} key={option.id}>
                                    {option.code ? `${option.name} - ${option.code}` : option.name}
                                </li>
                            )}
                            renderTags={(tagValue: PsfTemplateItem[], getTagProps): any => {
                                return tagValue.map((option, index) => (
                                    <Chip
                                        label={option.code ? `${option.name} - ${option.code}` : option.name}
                                        {...getTagProps({ index })} key={option.id}
                                    />
                                ))
                            }}
                            value={selectedPsfItems}
                            groupBy={(option: PsfTemplateItem): string => option.category}
                            renderInput={(params): any => <TextField {...params} label="PSF Code" />}
                            onChange={(_event: any, newValue: PsfTemplateItem[]): void => {
                                setSelectedPsfItems(newValue);
                            }}
                            limitTags={6}
                            multiple
                            filterSelectedOptions
                        />}

                        <ConditionBuilderComponent
                            keys={occurrenceTemplateKeys}
                            initialCondition={condition}
                            onChangeCondition={onChangeCondition}
                        />
                    </Stack>
                </FormGroup>
                <hr />
                <Typography variant='h6'>Occurrence DNA</Typography>
                <Stack direction='row' spacing={2} justifyContent='center'>
                    <Chip
                        label={`Successes: ${success}`}
                        color='success'
                    />
                    <Chip
                        label={`Failures: ${failure}`}
                        color='error'
                    />
                </Stack>

            </DialogContent>
            <DialogActions>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={isRunning ? <CircularProgress size={20} /> : null}
                    onClick={onClose}
                    disabled={isRunning}
                >
                    Ok
                </Button>
            </DialogActions>
        </Dialog>
    )
   
}
